import api from './api';
import { downloadBlobFile, openBlobFile } from './nativeFileService';

const parseFileNameFromDisposition = (headerValue) => {
  const raw = String(headerValue || '');
  const utf8Match = raw.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  const simpleMatch = raw.match(/filename="?([^"]+)"?/i);
  return simpleMatch?.[1] || '';
};

const extractQmsFileMeta = (headers = {}) => ({
  warning: headers?.['x-qms-warning'] || '',
  documentStatus: headers?.['x-qms-document-status'] || '',
  referenceOnly: String(headers?.['x-qms-reference-only'] || '').toLowerCase() === 'true',
  historicalRevision: String(headers?.['x-qms-historical-revision'] || '').toLowerCase() === 'true',
  suggestedFileName: parseFileNameFromDisposition(headers?.['content-disposition'] || '')
});

const qualityService = {
  getIsoCatalog: async () => {
    const response = await api.get('/quality/iso/catalog');
    return response.data;
  },

  getIsoTemplate: async (key) => {
    const response = await api.get(`/quality/iso/template/${encodeURIComponent(key)}`);
    return response.data;
  },

  getQmsDocuments: async (params = {}) => {
    const response = await api.get('/quality/qms/documents', { params });
    return response.data;
  },

  getQmsOverview: async () => {
    const response = await api.get('/quality/qms/overview');
    return response.data;
  },

  getQmsFormRecords: async (params = {}) => {
    const response = await api.get('/quality/qms/forms/records', { params });
    return response.data;
  },

  createQmsFormRecord: async (payload) => {
    const response = await api.post('/quality/qms/forms/records', payload);
    return response.data;
  },

  updateQmsFormRecord: async (id, payload) => {
    const response = await api.put(`/quality/qms/forms/records/${id}`, payload);
    return response.data;
  },

  generateQmsFormRecordFile: async (id) => {
    const response = await api.post(`/quality/qms/forms/records/${id}/generate`);
    return response.data;
  },

  signQmsFormRecord: async (id, payload) => {
    const response = await api.post(`/quality/qms/forms/records/${id}/sign`, payload);
    return response.data;
  },

  getQmsFormRecordSignatures: async (id) => {
    const response = await api.get(`/quality/qms/forms/records/${id}/signatures`);
    return response.data;
  },

  downloadQmsFormRecordFile: async (id, fallbackName = 'filled-qms-form.docx') => {
    const response = await api.get(`/quality/qms/forms/records/${id}/download`, {
      responseType: 'blob'
    });
    const contentDisposition = response.headers?.['content-disposition'] || '';
    const fileName = parseFileNameFromDisposition(contentDisposition) || fallbackName;
    await downloadBlobFile(response.data, {
      fileName,
      mimeType: response.headers?.['content-type'] || response.data?.type
    });
    return {
      fileName,
      fileHash: response.headers?.['x-qms-generated-hash'] || ''
    };
  },

  getQmsActions: async (params = {}) => {
    const response = await api.get('/quality/qms/actions', { params });
    return response.data;
  },

  getQmsAudits: async (params = {}) => {
    const response = await api.get('/quality/qms/audits', { params });
    return response.data;
  },

  getQmsAuditFindings: async () => {
    const response = await api.get('/quality/qms/audits/findings');
    return response.data;
  },

  getQmsTrainingAssignments: async () => {
    const response = await api.get('/quality/qms/training');
    return response.data;
  },

  getQmsCalibrationEquipment: async () => {
    const response = await api.get('/quality/qms/calibration');
    return response.data;
  },

  getQmsCalibrationEvents: async (id) => {
    const response = await api.get(`/quality/qms/calibration/${id}/events`);
    return response.data;
  },

  createQmsDocument: async (payload) => {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (key === 'file') {
        formData.append('file', value);
        return;
      }
      formData.append(key, value);
    });

    const response = await api.post('/quality/qms/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  updateQmsDocumentStatus: async (id, payload) => {
    const response = await api.put(`/quality/qms/documents/${id}/status`, payload);
    return response.data;
  },

  createQmsAction: async (payload) => {
    const response = await api.post('/quality/qms/actions', payload);
    return response.data;
  },

  createQmsAudit: async (payload) => {
    const response = await api.post('/quality/qms/audits', payload);
    return response.data;
  },

  createQmsCalibrationEquipment: async (payload) => {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (key === 'file') {
        formData.append('file', value);
        return;
      }
      formData.append(key, value);
    });

    const response = await api.post('/quality/qms/calibration', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  updateQmsCalibrationEquipment: async (id, payload) => {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (key === 'file') {
        formData.append('file', value);
        return;
      }
      formData.append(key, value);
    });

    const response = await api.put(`/quality/qms/calibration/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  recordQmsCalibrationEvent: async (id, payload) => {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (key === 'file') {
        formData.append('file', value);
        return;
      }
      formData.append(key, value);
    });

    const response = await api.post(`/quality/qms/calibration/${id}/events`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  updateQmsAudit: async (id, payload) => {
    const response = await api.put(`/quality/qms/audits/${id}`, payload);
    return response.data;
  },

  updateQmsAction: async (id, payload) => {
    const response = await api.put(`/quality/qms/actions/${id}`, payload);
    return response.data;
  },

  createQmsAuditFinding: async (auditId, payload) => {
    const response = await api.post(`/quality/qms/audits/${auditId}/findings`, payload);
    return response.data;
  },

  updateQmsAuditFinding: async (id, payload) => {
    const response = await api.put(`/quality/qms/audits/findings/${id}`, payload);
    return response.data;
  },

  assignQmsTraining: async (payload) => {
    const response = await api.post('/quality/qms/training/assign', payload);
    return response.data;
  },

  acknowledgeQmsTraining: async (id, payload) => {
    const response = await api.put(`/quality/qms/training/${id}/acknowledge`, payload);
    return response.data;
  },

  getQmsDocumentRevisions: async (id) => {
    const response = await api.get(`/quality/qms/documents/${id}/revisions`);
    return response.data;
  },

  uploadQmsDocumentRevision: async (id, payload) => {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (key === 'file') {
        formData.append('file', value);
        return;
      }
      formData.append(key, value);
    });

    const response = await api.put(`/quality/qms/documents/${id}/revision`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  openIsoDocument: async (key) => {
    const response = await api.get(`/quality/iso/file/${encodeURIComponent(key)}`, {
      responseType: 'blob'
    });

    await openBlobFile(response.data, {
      fileName: key || 'iso-document',
      mimeType: response.headers?.['content-type'] || response.data?.type
    });
  },

  downloadIsoDocument: async (key, fallbackName = 'iso-document') => {
    const response = await api.get(`/quality/iso/download/${encodeURIComponent(key)}`, {
      responseType: 'blob'
    });

    const contentDisposition = response.headers?.['content-disposition'] || '';
    const fileName = parseFileNameFromDisposition(contentDisposition) || fallbackName;
    await downloadBlobFile(response.data, {
      fileName,
      mimeType: response.headers?.['content-type'] || response.data?.type
    });
  },

  openQmsDocumentFile: async (id) => {
    const response = await api.get(`/quality/qms/documents/${id}/file`, {
      responseType: 'blob'
    });
    const fileMeta = extractQmsFileMeta(response.headers);

    await openBlobFile(response.data, {
      fileName: fileMeta.suggestedFileName || `qms-document-${id}`,
      mimeType: response.headers?.['content-type'] || response.data?.type
    });
    return fileMeta;
  },

  downloadQmsDocumentFile: async (id, fallbackName = 'qms-document') => {
    const response = await api.get(`/quality/qms/documents/${id}/download`, {
      responseType: 'blob'
    });

    const fileMeta = extractQmsFileMeta(response.headers);
    const contentDisposition = response.headers?.['content-disposition'] || '';
    const fileName = parseFileNameFromDisposition(contentDisposition) || fallbackName;
    await downloadBlobFile(response.data, {
      fileName,
      mimeType: response.headers?.['content-type'] || response.data?.type
    });
    return { ...fileMeta, suggestedFileName: fileName };
  },

  openQmsRevisionFile: async (revisionId) => {
    const response = await api.get(`/quality/qms/revisions/${revisionId}/file`, {
      responseType: 'blob'
    });
    const fileMeta = extractQmsFileMeta(response.headers);

    await openBlobFile(response.data, {
      fileName: fileMeta.suggestedFileName || `qms-revision-${revisionId}`,
      mimeType: response.headers?.['content-type'] || response.data?.type
    });
    return fileMeta;
  },

  downloadQmsRevisionFile: async (revisionId, fallbackName = 'qms-revision') => {
    const response = await api.get(`/quality/qms/revisions/${revisionId}/download`, {
      responseType: 'blob'
    });

    const fileMeta = extractQmsFileMeta(response.headers);
    const contentDisposition = response.headers?.['content-disposition'] || '';
    const fileName = parseFileNameFromDisposition(contentDisposition) || fallbackName;
    await downloadBlobFile(response.data, {
      fileName,
      mimeType: response.headers?.['content-type'] || response.data?.type
    });
    return { ...fileMeta, suggestedFileName: fileName };
  }
};

export default qualityService;
